import React from "react";
import { Grid, Select, Stack, TextInput } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import PhoneInput from "../../common/PhoneInput";
import { StaffPhotoPicker } from "../../common/StaffPhotoPicker";
import type {
  StaffFormValues,
  StaffRole,
  StaffStatus,
} from "../../../types/staffForm";
import { DisabledFieldHelp, LabelWithHelp } from "./FieldHelp";
import type { RoleOption } from "./staffFormUtils";
import styles from "./StaffForm.module.css";

const STATUS_HELP = "Only active employees will appear in reports and payroll";
const EMAIL_HELP = "To change your email, please email contact@getpagoda.com";

const STATUS_OPTIONS: { value: StaffStatus; label: string }[] = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

interface GeneralInfoPanelProps {
  form: UseFormReturnType<StaffFormValues>;
  isCreate: boolean;
  /** Editing your own record: role and status are locked. */
  isSelf: boolean;
  roleOptions: RoleOption[];
  onRoleChange: (role: StaffRole) => void;
  photo: {
    /** The saved photo or a local preview of a staged one. */
    url: string | null;
    error: string | null;
    onSelectFile: (file: File) => void;
    onRemove: () => void;
  };
}

/** General Info: photo first, then identity, role/status and contact fields. */
export function GeneralInfoPanel({
  form,
  isCreate,
  isSelf,
  roleOptions,
  onRoleChange,
  photo,
}: GeneralInfoPanelProps) {
  const values = form.getValues();
  return (
    <Stack gap="sm" className={styles.generalPanel}>
      <StaffPhotoPicker
        photoUrl={photo.url}
        firstName={values.firstName}
        lastName={values.lastName}
        onSelectFile={photo.onSelectFile}
        onRemove={photo.onRemove}
        error={photo.error}
      />

      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, xs: 6 }}>
          <TextInput
            label="First Name"
            required
            {...form.getInputProps("firstName")}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, xs: 6 }}>
          <TextInput
            label="Last Name"
            required
            {...form.getInputProps("lastName")}
          />
        </Grid.Col>
      </Grid>

      <TextInput
        label="Title"
        required
        placeholder="e.g., Hair Stylist, Nail Technician"
        {...form.getInputProps("title")}
      />

      <Select
        label="Role"
        required
        data={roleOptions}
        value={values.role}
        onChange={(role) => role && onRoleChange(role as StaffRole)}
        allowDeselect={false}
        disabled={isSelf}
        error={form.errors.role}
      />

      <DisabledFieldHelp disabled={isSelf} help={STATUS_HELP}>
        <Select
          label={<LabelWithHelp label="Status" help={STATUS_HELP} />}
          required
          data={STATUS_OPTIONS}
          allowDeselect={false}
          disabled={isSelf}
          {...form.getInputProps("status")}
        />
      </DisabledFieldHelp>

      <DisabledFieldHelp disabled={!isCreate} help={EMAIL_HELP}>
        <TextInput
          label={
            isCreate ? (
              "Email"
            ) : (
              <LabelWithHelp label="Email" help={EMAIL_HELP} />
            )
          }
          required
          disabled={!isCreate}
          {...form.getInputProps("email")}
        />
      </DisabledFieldHelp>

      <PhoneInput
        required
        {...form.getInputProps("phone")}
        value={values.phone}
        error={form.errors.phone}
      />
    </Stack>
  );
}
